import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_COLORS as C, APP_FONT as F, APP_SIZES as S } from './tokens';
import { SEARCH_PLACEHOLDER } from './constants';
import { fmtBadgeCount } from './helpers';
import type { AppUser } from './types';

type Props = {
  user: AppUser;
  unreadInbox?: number;
  unreadNotifications?: number;
  onCreateClick?: () => void;
  onVelaClick?: () => void;
  onNotificationsClick?: () => void;
  onSearchFocus?: () => void;
  onLogout?: () => void;
};

const Icon = ({ children, size = 19 }: { children: React.ReactNode; size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const iconBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  borderRadius: '50%',
  width: 36,
  height: 36,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  color: C.ink,
  padding: 0,
};

function readCompanyName(): string {
  try {
    const keys = ['company_name', 'organization_name', 'business_name'];
    for (const k of keys) {
      const v = typeof window !== 'undefined' ? window.localStorage.getItem(k) : null;
      if (v && v.trim()) return v.trim();
    }
    const u = typeof window !== 'undefined' ? window.localStorage.getItem('user') : null;
    if (u) {
      try {
        const o = JSON.parse(u);
        const n = o && (o.company_name || o.companyName || o.organization_name || o.business_name);
        if (n && String(n).trim()) return String(n).trim();
      } catch (e) {}
    }
  } catch (e) {}
  return '';
}

export default function AppHeader(props: Props) {
  const { user, unreadInbox = 0, unreadNotifications = 0 } = props;
  const companyName = readCompanyName();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchExpanded) return;
    const onDocClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [searchExpanded]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    if (props.onLogout) {
      props.onLogout();
      return;
    }
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    window.location.href = '/login';
  };

  const handleManageAccount = () => {
    setMenuOpen(false);
    navigate('/settings');
  };

  const bg = (key: string): React.CSSProperties => (
    hovered === key ? { background: C.pillHover } : {}
  );

  const userEmail = (user as any).email || '';
  const initial = ((user.name || user.initials || '?').trim().charAt(0) || '?').toUpperCase();

  return (
    <header style={{
      background: '#fff',
      borderBottom: '1px solid ' + C.line,
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      fontFamily: F.family,
      height: S.headerHeight,
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      {/* Brand lives in the sidebar; header shows the company name if the user has set one */}
      <div
        onClick={() => navigate('/')}
        style={{ minWidth: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', maxWidth: 220 }}
        aria-label="Novala home"
      >
        {companyName && (
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: C.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 220,
          }}>
            {companyName}
          </span>
        )}
      </div>

      {/* Search - CENTERED in header via absolute positioning. Expands on click, collapses on outside click */}
      <div
        ref={searchRef}
        onClick={() => {
          setSearchExpanded(true);
          if (props.onSearchFocus) props.onSearchFocus();
          navigate('/search');
        }}
        style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          width: searchExpanded ? 480 : 320,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 14px', background: C.page, borderRadius: 999,
          fontSize: 13, color: C.sub, cursor: 'text', minWidth: 0,
          transition: 'width 220ms ease',
        }}>
        <span style={{ color: C.sub, flexShrink: 0, display: 'inline-flex' }}>
          <Icon size={16}><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></Icon>
        </span>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {SEARCH_PLACEHOLDER}
        </span>
      </div>

      {/* Right-side actions (pushed to the right since search is no longer flex:1) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
        <button
          onMouseEnter={() => setHovered('contact')}
          onMouseLeave={() => setHovered(null)}
          onClick={() => navigate('/help')}
          style={{
            background: hovered === 'contact' ? C.pillHover : 'transparent',
            border: '1px solid ' + C.line, color: C.ink, borderRadius: 8,
            padding: '7px 12px', fontSize: 12.5, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 7,
            cursor: 'pointer', marginRight: 4,
          }}>
          <span style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'linear-gradient(135deg, #F59E0B, #DC2626)',
            display: 'grid', placeItems: 'center', color: '#fff',
            fontWeight: 700, fontSize: 10,
          }}>CE</span>
          Contact experts
        </button>

        <button onMouseEnter={() => setHovered('inbox')} onMouseLeave={() => setHovered(null)} onClick={() => navigate('/documents')} style={{ ...iconBtnStyle, ...bg('inbox') }} title="Inbox">
          <Icon><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5 5h14l3 7v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5z"/></Icon>
          {unreadInbox > 0 && <span style={dotStyle} />}
        </button>

        <button onMouseEnter={() => setHovered('apps')} onMouseLeave={() => setHovered(null)} onClick={() => navigate('/')} style={{ ...iconBtnStyle, ...bg('apps') }} title="Apps">
          <Icon><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></Icon>
        </button>

        <button onMouseEnter={() => setHovered('help')} onMouseLeave={() => setHovered(null)} onClick={() => navigate('/help')} style={{ ...iconBtnStyle, ...bg('help') }} title="Help">
          <Icon><circle cx="12" cy="12" r="10"/><path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4"/><circle cx="12" cy="17" r="0.6" fill="currentColor"/></Icon>
        </button>

        <button
          onMouseEnter={() => setHovered('notif')}
          onMouseLeave={() => setHovered(null)}
          onClick={() => { if (props.onNotificationsClick) { props.onNotificationsClick(); } else { navigate('/documents'); } }}
          style={{ ...iconBtnStyle, ...bg('notif') }} title="Notifications">
          <Icon><path d="M18 16v-5a6 6 0 00-12 0v5l-2 3h16z"/><path d="M10 20a2 2 0 004 0"/></Icon>
          {unreadNotifications > 0 && (
            <span style={{ ...dotStyle, minWidth: 14, height: 14, borderRadius: 999, padding: '0 3px', fontSize: 9, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, background: C.red }}>
              {fmtBadgeCount(unreadNotifications)}
            </span>
          )}
        </button>

        <button
          onMouseEnter={() => setHovered('settings')}
          onMouseLeave={() => setHovered(null)}
          onClick={() => navigate('/settings')}
          style={{ ...iconBtnStyle, ...bg('settings') }} title="Settings">
          <Icon><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></Icon>
        </button>

        {/* Avatar + dropdown */}
        <div ref={menuRef} style={{ position: 'relative', marginLeft: 2 }}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            title={user.name}
            aria-label="User menu"
            style={{
              width: 32, height: 32, borderRadius: '50%', background: C.teal,
              color: '#fff', display: 'grid', placeItems: 'center',
              fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0,
              border: 0,
              outline: menuOpen ? '2px solid ' + C.ink : 'none',
              outlineOffset: 2,
            }}>
            {initial}
          </button>

          {menuOpen && (
            <div style={{
              position: 'fixed', top: 60, right: 14,
              width: 260, background: '#fff', color: C.ink,
              border: '1px solid ' + C.line, borderRadius: 10,
              boxShadow: '0 10px 30px rgba(14,26,26,0.18)',
              padding: '6px 0', zIndex: 10000,
              fontFamily: F.family,
            }}>
              {/* User header */}
              <div style={{ padding: '12px 14px', borderBottom: '1px solid ' + C.line, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: C.teal, color: '#fff', fontWeight: 700, fontSize: 14, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  {initial}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{user.name}</div>
                  {userEmail && <div style={{ fontSize: 11, color: C.sub, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</div>}
                </div>
              </div>

              {/* Settings */}
              <div onClick={handleManageAccount} onMouseEnter={(e)=>{(e.currentTarget as HTMLDivElement).style.background='#F1F5F9';}} onMouseLeave={(e)=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}} style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: C.ink, cursor: 'pointer', fontWeight: 500 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                Settings
              </div>

              {/* Help & Support */}
              <div onClick={()=>{setMenuOpen(false); navigate('/help');}} onMouseEnter={(e)=>{(e.currentTarget as HTMLDivElement).style.background='#F1F5F9';}} onMouseLeave={(e)=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}} style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: C.ink, cursor: 'pointer', fontWeight: 500 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Help &amp; Support
              </div>

              {/* Keyboard Shortcuts */}
              <div onClick={()=>{setMenuOpen(false); navigate('/keyboard-shortcuts');}} onMouseEnter={(e)=>{(e.currentTarget as HTMLDivElement).style.background='#F1F5F9';}} onMouseLeave={(e)=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}} style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: C.ink, cursor: 'pointer', fontWeight: 500 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10"/></svg>
                Keyboard Shortcuts
              </div>

              {/* What's New */}
              <div onClick={()=>{setMenuOpen(false); navigate('/whats-new');}} onMouseEnter={(e)=>{(e.currentTarget as HTMLDivElement).style.background='#F1F5F9';}} onMouseLeave={(e)=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}} style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: C.ink, cursor: 'pointer', fontWeight: 500 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                What&apos;s New
              </div>

              {/* Send Feedback */}
              <div onClick={()=>{setMenuOpen(false); navigate('/feedback');}} onMouseEnter={(e)=>{(e.currentTarget as HTMLDivElement).style.background='#F1F5F9';}} onMouseLeave={(e)=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}} style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: C.ink, cursor: 'pointer', fontWeight: 500 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                Send Feedback
              </div>

              <div style={{ height: 1, background: C.line, margin: '4px 10px' }}></div>

              {/* Sign out */}
              <div onClick={handleLogout} onMouseEnter={(e)=>{(e.currentTarget as HTMLDivElement).style.background='#F1F5F9';}} onMouseLeave={(e)=>{(e.currentTarget as HTMLDivElement).style.background='transparent';}} style={{ padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: '#DC2626', cursor: 'pointer', fontWeight: 500 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign out
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => { if (props.onVelaClick) { props.onVelaClick(); } else { navigate('/agents'); } }}
          title="Ask Vela"
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, ' + C.vela + ', #8B5CF6)',
            color: '#fff', display: 'grid', placeItems: 'center',
            fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 0,
            marginLeft: 2, boxShadow: '0 2px 8px rgba(107,70,193,0.3)',
          }}>
          V
        </button>
      </div>
    </header>
  );
}

const dotStyle: React.CSSProperties = {
  position: 'absolute',
  top: 6,
  right: 8,
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: '#B53B2E',
  border: '1.5px solid #fff',
};
